import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, Trend, Dashboard, WidgetValues, IValueViewModel, DigitalSetSimpleDictionary, AggregationMode, TagStringValueDto, Dictionary } from '../../../models';
import { ITrendService, IDashboardService, ILoggerService, IAuthorizationService, INavigationHistoryService, IStoreService, IDashboardLoadService, IDateNavigationService, IDateRangeService, IDigitalSetHelperService, IStringValuesHelperService, IStringValuesService } from '../../../services';
import { Roles, editWidgetState, treeUpdated, refreshClick, aggregationModes } from '../../../common';

import { IRootScope } from 'IRootScope';
import { TagsTrendConfiguration } from '../../../models/widgets/TagsTrendConfiguration/tagsTrendConfiguration';
import { IWidgetFactory, IWidget } from '../../../widgets';
import { Store, StoreElement } from '../../../stateManagement';

class TrendsStateController implements angular.IComponentController {
    currentBeginDate: string;
    currentEndDate: string;
    currentInterval: any;
    dashboardList: any[];
    dashboardName: string;
    dashboardType: 'new' | 'existing';
    data: WidgetValues = { entries: [] } as WidgetValues;
    digitalSets: DigitalSetSimpleDictionary;
    hasManualInputRole: boolean;
    isLoaded: boolean;
    isLoading: boolean;
    loadedSeries: any[];
    loadingMessage: string;
    maximized: string;
    pinName: string;
    savedTrend: Trend;
    stringValues: TagStringValueDto[];
    trend: IWidget;
    trendGuid: string;
    viewModels: Dictionary<IValueViewModel>;

    private _selectedDashboard: any;
    private _timeout: any;
    private _garbage: (() => void)[] = [];

    static $inject = [
        '$q',
        '$rootScope',
        '$scope',
        '$state',
        '$timeout',
        'storeService',
        'dashboardService',
        'translatedLoggerService',
        'authorizationService',
        'widgetFactory',
        'trendService',
        'navigationHistoryService',
        'dashboardLoadService',
        'dateRangeService',
        'digitalSetHelperService',
        'stringValuesService',
        'stringValuesHelperService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$state: angular.ui.IStateService,
        private _$timeout: angular.ITimeoutService,
        private _storeService: IStoreService,
        private _dashboardService: IDashboardService,
        private _translatedLoggerService: ILoggerService,
        private _authorizationService: IAuthorizationService,
        private _widgetFactory: IWidgetFactory,
        private _trendService: ITrendService,
        private _navigationHistoryService: INavigationHistoryService,
        private _dashboardLoadService: IDashboardLoadService,
        private _dateRangeService: IDateRangeService,
        private _digitalSetHelperService: IDigitalSetHelperService,
        private _stringValuesService: IStringValuesService,
        private _stringValuesHelperService: IStringValuesHelperService
    ) {
        this.hasManualInputRole = _authorizationService.isInRoles(Roles.manualInputRoles);
    }

    $onInit() {
        try {
            this.dashboardType = 'new';
            this.loadedSeries = !(<any>window).trendSeries && window.opener
                ? JSON.parse((<any>window).opener.trendSeries)
                : (<any>window).trendSeries
                    ? JSON.parse((<any>window).trendSeries)
                    : undefined;
        } catch (e) {
            this.loadedSeries = undefined;
        }

        this.maximized = this._$state.params['maximized'];
        this.trendGuid = this._$state.params['trendGuid'];

        this._garbage.push(this._$scope.$watchGroup(
            [() => this.currentBeginDate, () => this.currentEndDate],
            () => this.currentInterval = { startDate: this.currentBeginDate, endDate: this.currentEndDate }
        ));

        this._garbage.push(
            this._storeService.register(
                store => this.storeUpdate(store),
                [StoreElement.Dashboards, StoreElement.DigitalSets, StoreElement.ValueViewModels]
            )
        );

        this.initLoadWidget()
            .then(() => this.refresh(this.currentBeginDate, this.currentEndDate, false));

        this._garbage.push(this._$scope.$on(refreshClick, (event, data) => {
            this.refresh(data.beginDate, data.endDate, data.force);
        }));
    }

    $onDestroy() {
        this.cancelTimeout();
        this._garbage.forEach(g => g());
    }

    private initLoadWidget(): angular.IPromise<void> {
        if (this.trendGuid) {
            return this._trendService
                .getTrend(this.trendGuid)
                .then(trend => {
                    this.savedTrend = trend;
                    this.pinName = trend.name;
                    this.updateHistoryName(this.pinName);
                    return this.loadWidget();
                })
                .catch(reason => {
                    this._translatedLoggerService.logError(`Cannot read saved trend :${reason.message}`, true);
                });
        } else {
            this.loadWidget();
            return this._$q.when();
        }
    }

    private storeUpdate(store: Store): void {
        this.dashboardList = _.values(store.dashboards.dictionary);
        this.digitalSets = store.digitalSetDictionary.dictionary;
        this.viewModels = store.valueViewModels.dictionary;
        this.isLoaded = true;
        this.isLoading = false;
    }

    private refresh(beginDate: string, endDate: string, force: boolean): angular.IPromise<WidgetValues> {
        if (this.trend && this._dateRangeService.initialized() && beginDate && endDate) {
            this.loadingMessage = 'Loading data';
            this.isLoading = true;
            return this._dashboardLoadService
                .refresh([this.trend], beginDate, endDate, null, null, null, force)
                .then(data => {
                    return this._stringValuesService
                        .getAll()
                        .then(response => {
                            this.stringValues = response;
                            const trendData = _.find(data, d => d.guid === this.trend.guid);
                            let preparedData = this._digitalSetHelperService.mapDigitalSetsValues([trendData], this.digitalSets, this.viewModels, AggregationMode.None);
                            preparedData = this._stringValuesHelperService.mapStringValues(preparedData, this.stringValues, this.viewModels, AggregationMode.None);
                            this.data = preparedData && preparedData.length > 0
                                ? preparedData[0]
                                : undefined;
                            return this.data;
                        });
                })
                .finally(() => this.isLoading = false);
        } else {
            return this._$q.when(undefined);
        }
    }

    private cancelTimeout() {
        if (this._timeout) {
            this._$timeout.cancel(this._timeout);
            this._timeout = undefined;
        }
    }


    private updateHistoryName(name: string) {
        this._navigationHistoryService.updateLastHistory(name, 'trend');
    }

    private loadWidget() {
        const self = this;
        const series = (this.loadedSeries ? this.loadedSeries : this.savedTrend ? this.savedTrend.configuration.series : []);
        const widgetConfig = this.getTagsTrendConfiguration(series);
        this.trend = this._widgetFactory.createWidget('tagsTrend', widgetConfig);
        this.trend.configuration = widgetConfig;
        this._garbage.push(this._$scope.$watchCollection(
            () => (<any>self.trend).configuration.series,
            (newValue) => {
                _.forEach(newValue, s => {
                    this._garbage.push(this._$scope.$watchCollection(
                        () => s,
                        () => (<any>self.trend).isPinWarning = true
                    ));
                });
            }
        ));
        this._garbage.push(this._$scope.$watch(
            () => this.trend.configuration.series,
            () => this.refresh(this.currentBeginDate, this.currentEndDate, false),
            true
        ));
        this.initPin();
    }

    private initPin() {
        (<any>this.trend).unPin = this.unPin;
        this.cancelTimeout();
        this._timeout = this._$timeout(() => {
            (<any>this.trend).isPinned = this.savedTrend ? true : false;
            (<any>this.trend).isPinWarning = false;
        });
    }


    private getTagsTrendConfiguration(series: any[]): TagsTrendConfiguration {
        const configuration = new TagsTrendConfiguration();

        configuration.options.isHorizontalGridlinesVisible = true;
        configuration.options.isTitleVisible = false;
        configuration.options.isLegendVisible = false;
        configuration.isEndDateIncluded = true;
        configuration.series = series;
        configuration.nameConfiguration.hasName = true;
        configuration.nameConfiguration.hasUnit = true;

        return configuration;
    }

    private addWidget(dashboard) {
        const self = this;

        try {
            this.trend.configuration.options = {
                isHorizontalGridlinesVisible: false,
                isTitleVisible: false,
                isLegendVisible: false,
                display: {}
            };
            this._dashboardService
                .addWidget(dashboard.guid, this.trend)
                .then(d => {
                    self._translatedLoggerService.logSuccess(`Successfuly saved widget to dashboard ${dashboard.name}`);
                    self._selectedDashboard = undefined;
                    self.trend = _.find(d.configuration.widgets, (w: any) => w.guid === self.trend.guid);
                    self._$state.go(editWidgetState, {
                        dashboardGuid: dashboard.guid,
                        widgetGuid: self.trend.guid,
                        beginDate: this.currentBeginDate ?? '',
                        endDate: this.currentEndDate ?? ''
                    });
                    this._storeService.dispatchers().valueViewModels.getAll(true);
                }, (error) => {
                    self._translatedLoggerService.logError(error);
                });
        } catch (e) {
            this._translatedLoggerService.logError(e);
        }
    }

    private addOrUpdateTrend(trend: Trend): angular.IPromise<any> {
        const promise = (this.savedTrend && this.savedTrend.guid) ?
            this._trendService.updateTrend(trend) :
            this._trendService.addTrend(trend);
        return promise
            .then(data => {
                this._storeService.dispatchers().valueViewModels.getAll(true);
                return data;
            });
    }

    pin() {
        if (this.pinName) {
            const trend: Trend = {
                name: this.pinName,
                guid: (this.savedTrend) ? this.savedTrend.guid : null,
                configuration: {
                    series: (<any>this.trend.configuration).series
                }
            };
            this.addOrUpdateTrend(trend)
                .then(data => {
                    this._translatedLoggerService.logSuccess(`Successfuly saved trend ${this.pinName}`);
                    this.savedTrend = data;
                    this._$state.go('trend', { trendGuid: this.savedTrend.guid, beginDate: '', endDate: '', maximzed: false }, { notify: false });
                    this._$rootScope.$broadcast(treeUpdated);
                })
                .catch(reason => this._translatedLoggerService.logError(`Unable to save trend ${this.pinName} : ${reason.message}`))
                .finally(() => this.initPin());
        } else {
            this._translatedLoggerService.logError(`Unable to save trend without a name`);
        }
    }

    unPin() {
        this._trendService.deleteTrend(this.savedTrend.guid)
            .then(() => {
                this._storeService.dispatchers().valueViewModels.getAll(true);
                this._translatedLoggerService.logSuccess(`Successfuly deleted trend ${this.pinName}`);
                this._$state.go('trends', { trendGuid: null }, { notify: false });
                this.savedTrend = null;
                this.pinName = null;
                this.initPin();
                this._$rootScope.$broadcast(treeUpdated);
            }).catch(reason => {
                this._translatedLoggerService.logError(`Unable to delete trend ${this.pinName} : ${reason.message}`);
            });
    }

    saveToDashboard(): void {
        const dashboardType = this.dashboardType;
        if (dashboardType === 'existing') {
            this.saveToExistingDashboard();
        } else if (dashboardType === 'new') {
            this.saveToNewDashboard();
        }
    }

    private saveToExistingDashboard() {
        if (this._selectedDashboard) {
            this.addWidget(this._selectedDashboard);
        } else {
            this._translatedLoggerService.logWarning('A dashboard has to be selected');
        }
    }

    private saveToNewDashboard() {
        try {
            const dashboard = new Dashboard();
            dashboard.name = this.dashboardName ? this.dashboardName : '';

            this._storeService.dispatchers().dashboards
                .addOne(dashboard, true)
                .then(response => {
                    const newDashboard = _.find(response, r => r.guid === dashboard.guid);
                    if (newDashboard) {
                        this.addWidget(newDashboard);
                    }
                    return this._storeService.dispatchers().valueViewModels.getAll(true);
                });
        } catch (e) {
            this._translatedLoggerService.logError(e);
        }
    }

    selectDashboard(dashboard: any): void {
        this._selectedDashboard = dashboard;
    }

    isSelected(dashboardGuid: string): boolean {
        return this._selectedDashboard && this._selectedDashboard.guid === dashboardGuid;
    }

    isValid(): boolean {
        let isValid: boolean;

        isValid = this.dashboardType === 'existing'
            ? !!this._selectedDashboard
            : !!this.dashboardName;
        isValid = isValid && !!this.trend.name;

        return isValid;
    }

    canEdit(): boolean {
        const series = this.trend?.configuration?.series;
        return this._authorizationService.canEdit() && (!series || series.filter(s => !!s.rawInformation).length === 0);
    }

    canSave(pinName: string): boolean {
        return !!pinName;
    }
}

export const wtTrendsState: ComponentDefinition = {
    componentId: 'wtTrendsState',
    controller: TrendsStateController,
    template: require('./trendsState.component.html'),
    controllerAs: 'vm',
    bindings: {

    }
};